import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";

import { Flex, P, StyledLink } from "orcs-design-system";
import { isNil } from "lodash";
import {
  heading,
  noOptionsLabel,
  noOptionsLinkLabel,
  searchErrorLabel,
} from "src/components/Search/Search.config";
import URL, { url, getSearchPath } from "src/consts/urlPaths";
import { createPlaceholderData } from "src/util/search";
import { filterTypes } from "../SearchPage.config";

import SearchResults from "./SearchResults";

const DISPLAY_ALL_MAX = 10;
const PLACEHOLDER_COUNT = 3;

const Results = ({
  isLoadingPeople,
  isLoadingTeams,
  isLoadingTags,
  onLoadMore,
  formattedData,
  searchFilter,
  searchTerm,
  workspace,
  compactViewMore = false,
  searchError,
  emptyStateComponent,
}) => {
  const isLoading = isLoadingPeople || isLoadingTeams || isLoadingTags;
  const haveSearchTerm = !!searchTerm?.length;
  const haveData = !isNil(formattedData);

  const [results, setResults] = useState([]);

  const placeholders = useMemo(() => {
    const data = createPlaceholderData(PLACEHOLDER_COUNT).map((p) =>
      p.options.map((o) => ({
        ...o,
        isPlaceholder: true,
      }))
    );

    return {
      people: data[0],
      teams: data[1],
      tags: data[2],
    };
  }, []);

  useEffect(() => {
    // search filters
    const isAllSearch = searchFilter === filterTypes.ALL;
    const isPeopleSearch = searchFilter === filterTypes.PEOPLE;
    const isTeamSearch = searchFilter === filterTypes.TEAMS;
    const isTagSearch = searchFilter === filterTypes.TAGS;

    // populate with loading placeholders (when loading or data not yet processed)?
    const placeholdPeople = isLoadingPeople || isNil(formattedData?.people);
    const placeholdTeams = isLoadingTeams || isNil(formattedData?.teams);
    const placeholdTags = isLoadingTags || isNil(formattedData?.tags);

    const peopleOptions = formattedData?.people?.options ?? [];
    const teamOptions = formattedData?.teams?.options ?? [];
    const tagOptions = formattedData?.tags?.options ?? [];

    const displayLimit = isAllSearch ? DISPLAY_ALL_MAX : undefined;

    const resultsSets = [];

    if (isAllSearch || isPeopleSearch) {
      const collection = placeholdPeople
        ? [...peopleOptions, ...placeholders.people]
        : peopleOptions;

      resultsSets.push(
        <SearchResults
          key="people"
          header={heading.people}
          collection={collection}
          onLoadMore={onLoadMore}
          selectedString={searchTerm}
          foundCount={formattedData?.people?.counters?.found}
          totalCount={formattedData?.people?.counters?.total}
          limitCount={displayLimit}
          viewMoreURL={getSearchPath(filterTypes.PEOPLE, searchTerm)}
          workspace={workspace}
          compactViewMore={compactViewMore}
        />
      );
    }

    if (isAllSearch || isTeamSearch) {
      const collection = placeholdTeams
        ? [...teamOptions, ...placeholders.teams]
        : teamOptions;

      resultsSets.push(
        <SearchResults
          key="teams"
          header={heading.teams}
          collection={collection}
          onLoadMore={onLoadMore}
          selectedString={searchTerm}
          foundCount={formattedData?.teams?.counters?.found}
          totalCount={formattedData?.teams?.counters?.total}
          limitCount={displayLimit}
          viewMoreURL={getSearchPath(filterTypes.TEAMS, searchTerm)}
          workspace={workspace}
          compactViewMore={compactViewMore}
        />
      );
    }

    if (isAllSearch || isTagSearch) {
      const collection = placeholdTags
        ? [...tagOptions, ...placeholders.tags]
        : tagOptions;

      resultsSets.push(
        <SearchResults
          key="tags"
          header={heading.tags}
          collection={collection}
          onLoadMore={onLoadMore}
          selectedString={searchTerm}
          foundCount={formattedData?.tags?.counters?.found}
          totalCount={formattedData?.tags?.counters?.total}
          limitCount={displayLimit}
          viewMoreURL={getSearchPath(filterTypes.TAGS, searchTerm)}
          workspace={workspace}
          compactViewMore={compactViewMore}
        />
      );
    }

    setResults((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(resultsSets)) {
        return prev;
      }
      return resultsSets;
    });
  }, [
    setResults,
    compactViewMore,
    formattedData,
    isLoadingPeople,
    isLoadingTags,
    isLoadingTeams,
    onLoadMore,
    searchFilter,
    placeholders,
    searchTerm,
    workspace,
  ]);

  if (!isLoading && !haveSearchTerm && !haveData) {
    if (searchError) {
      return (
        <Flex justifyContent="center" p="s">
          <P color="black" fontSize="1">
            {searchErrorLabel}
          </P>
        </Flex>
      );
    }

    if (emptyStateComponent) {
      return (
        <Flex justifyContent="center" p="s">
          {emptyStateComponent}
        </Flex>
      );
    }

    return (
      <Flex justifyContent="center" p="s">
        <P color="black" fontSize="1">
          {noOptionsLabel}{" "}
          <StyledLink fontSize="1" to={url(URL.EXPLORE)}>
            {noOptionsLinkLabel}
          </StyledLink>
        </P>
      </Flex>
    );
  }

  if (results?.length) {
    return results;
  }

  return null;
};

Results.propTypes = {
  isLoadingPeople: PropTypes.bool,
  isLoadingTeams: PropTypes.bool,
  isLoadingTags: PropTypes.bool,
  onLoadMore: PropTypes.func,
  formattedData: PropTypes.object,
  searchFilter: PropTypes.string,
  searchTerm: PropTypes.string,
  workspace: PropTypes.object,
  compactViewMore: PropTypes.bool,
  searchError: PropTypes.object,
  emptyStateComponent: PropTypes.node,
};

export default Results;
